/**
 * Aggiunta a watchlist e wishlist dalla vista di dettaglio del titolo (F-071).
 *
 * Il gesto esisteva solo dalle pagine delle due liste, cioè da dove il titolo
 * *già* si trova: la vista di dettaglio — l'unico posto in cui un titolo esiste
 * di per sé — poteva soltanto dichiarare l'appartenenza, non stabilirla.
 *
 * **Il ticker viene prima della lista, ed è la ragione per cui questi due
 * pulsanti non sono un `mutate` in una riga.** Un titolo senza ticker di
 * quotazione risolto entra in lista senza errori e poi resta lì muto, senza un
 * prezzo, senza variazione e senza analisi: il fallimento arriva ore dopo, in
 * un posto diverso da dove è stato causato. Perciò, se il ticker manca, il
 * click apre prima il dialog di match già esistente (`MappingCandidatesModal`,
 * F-040 — candidati OpenFIGI già persistiti più inserimento manuale col
 * suffisso di borsa) e l'aggiunta prosegue **solo** se un ticker è stato
 * davvero associato. Annullare il dialog non aggiunge niente: è la stessa
 * distinzione che il dialog fa fra "risolto" e "chiuso".
 */

import { useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { Button } from '@/ui';
import { useAddWatchlistItem } from '@/features/watchlist/api';
import { MappingCandidatesModal } from '@/features/watchlist/MappingCandidatesModal';
import { useWishlist } from '@/features/wishlist/api';
import { WishlistItemModal } from '@/features/wishlist/WishlistItemModal';

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'Errore imprevisto';
}

type Instrument = { id: string; name: string; symbol: string | null };

/** Passaggio dal dialog di match, quando serve, prima dell'azione vera. */
function useTickerGate(instrument: Instrument, required: boolean, proceed: () => void) {
  const [open, setOpen] = useState(false);

  const start = () => {
    if (required) {
      setOpen(true);
      return;
    }
    proceed();
  };

  const modal = open ? (
    <MappingCandidatesModal
      instrumentId={instrument.id}
      instrumentName={instrument.name}
      onClose={() => setOpen(false)}
      onResolved={proceed}
    />
  ) : null;

  return { start, modal };
}

export function AddToWatchlistButton({
  instrument,
  needsTicker,
}: {
  instrument: Instrument;
  needsTicker: boolean;
}) {
  const addItem = useAddWatchlistItem();
  const { start, modal } = useTickerGate(instrument, needsTicker, () =>
    addItem.mutate({ instrumentId: instrument.id }),
  );

  return (
    <>
      <Button
        variant="secondary"
        loading={addItem.isPending}
        onClick={start}
        title={
          needsTicker
            ? `Associa il ticker e aggiungi ${instrument.name} alla watchlist`
            : `Aggiungi ${instrument.name} alla watchlist`
        }
      >
        ☆ Aggiungi a watchlist
      </Button>
      {addItem.isError && (
        <p className="idetail__muted" role="alert">
          {errorMessage(addItem.error)}
        </p>
      )}
      {modal}
    </>
  );
}

export function AddToWishlistButton({
  instrument,
  needsTicker,
}: {
  instrument: Instrument;
  needsTicker: boolean;
}) {
  const wishlist = useWishlist();
  const [formOpen, setFormOpen] = useState(false);
  const { start, modal } = useTickerGate(instrument, needsTicker, () => setFormOpen(true));

  return (
    <>
      <Button
        variant="secondary"
        onClick={start}
        title={
          needsTicker
            ? `Associa il ticker e candida ${instrument.name} all'acquisto`
            : `Candida ${instrument.name} all'acquisto`
        }
      >
        + Aggiungi a wishlist
      </Button>
      {modal}
      {formOpen && (
        <WishlistItemModal
          instrument={instrument}
          // Finché la wishlist non è caricata si usa la finestra di default
          // di SPEC-012.2: il campo resta vuoto e il suggerimento è l'unica
          // cosa che ne risente, mai la scadenza davvero salvata.
          defaultExpiryDays={wishlist.data?.defaultExpiryDays ?? 30}
          onClose={() => setFormOpen(false)}
        />
      )}
    </>
  );
}
